import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MessageSquare, Pencil, Trash2 } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { formatDateTime } from '../lib/format'
import { useAuthStore } from '../stores/authStore'
import { useSpaceAccess } from '../lib/useSpaceAccess'
import { Avatar } from './Avatar'
import { ConfirmDialog } from './ui/ConfirmDialog'

/** Only the platform Super Admin is read-only on tenant comment threads
 *  — they observe across tenants for oversight but aren't a participant
 *  in any one tenant's discussions. Company Admins and Sub Admins ARE
 *  participants: the backend's `assertTaskAccess` grants them a synth-
 *  admin `full` membership, so they can post / edit / delete their own
 *  comments. This client-side check just hides the compose UI for the
 *  read-only role. */
function isReadOnlyCommentRole(role) {
  return role === 'super_admin'
}

/** Tenant admins (Company Admin / Sub Admin) carry full comment access
 *  tenant-wide via that synth-admin membership, so the client must NOT
 *  gate them on the per-Space `canComment` flag — they're usually not a
 *  formal Space member, which would otherwise read as read-only and hide
 *  the compose box even though the backend would accept the post. */
function isFullCommentAdminRole(role) {
  return role === 'company_admin' || role === 'sub_admin'
}

/**
 * Reusable comments cell + popup for any task-row list (Space List
 * view, Task Monitor, Team Tasks, etc.).
 *
 * `CommentsCell` renders the trigger: a small chat-icon button
 * showing either the live count (when > 0) or the word "Comment" /
 * "—" when zero. Clicking it opens a portal-positioned panel below
 * the trigger with the full `TaskComments` thread (read + post +
 * delete-own).
 *
 * Both pieces were originally inlined inside SpacePage; extracting
 * them keeps the popup behaviour identical across every list view
 * that wants the same read+write affordance. The trigger's
 * "zero state" can be tuned per-page via the `zeroLabel` prop —
 * Space List uses "Comment" (verb, encourages adding one), while
 * Task Monitor / Team Tasks prefer a quiet `"—"` since those pages
 * are surveyance-focused.
 */
export function CommentsCell({
  taskId,
  currentUserId,
  /** Initial count from the row query (e.g. Task Monitor's
   *  `t.commentCount`). Used while the popup-scoped fetch is still
   *  in flight; the live fetch supersedes it once it lands so the
   *  number stays accurate after the user posts/deletes. */
  initialCount = null,
  /** Text rendered when there are zero comments. "Comment" reads as
   *  a verb (encourages action). "—" reads as a passive "no data".
   *  Defaults to "Comment". */
  zeroLabel = 'Comment',
  /** When true, the cell eagerly fetches the count on mount instead
   *  of waiting for the user to open the popup. SpacePage opts in
   *  because its task fetcher doesn't return `commentCount`; the
   *  heavier list views (Task Monitor / Team Tasks) leave this
   *  `false` so they don't fire N requests on mount and instead
   *  feed the count through `initialCount`. */
  alwaysFetchCount = false,
  /** Parent Space id — forwarded to the thread so the compose box is
   *  gated on the viewer's per-Space access level (read-only members
   *  can't post). Optional; without it the backend still enforces. */
  spaceId,
}) {
  // Tenant admins (Company Admin / Super Admin) have READ-ONLY
  // access — they see the count + the thread, but the compose
  // textarea and "delete own comment" affordances stay hidden.
  // This matches the docstring on `isReadOnlyCommentRole` and the
  // documented intent that admins use Task Monitor for oversight,
  // while still letting them read the team's discussion thread so
  // they can monitor task context. The backend's `assertTaskAccess`
  // already grants admins access via the synth-admin membership in
  // `workspace-access.js`, so the GET succeeds; the prior frontend
  // build was hiding the cell unilaterally and contradicting that.
  const actorRole = useAuthStore((s) => s.user?.role)
  const isReadOnly = isReadOnlyCommentRole(actorRole)

  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const [coords, setCoords] = useState(null)

  // Lightweight count fetch — keeps the cell numeric without paying
  // for the full thread on every list render. The popover refetches
  // the full body when it opens. Stale-while-revalidate behaviour
  // comes from React Query.
  const countQuery = useQuery({
    queryKey: ['task-comments', taskId],
    queryFn: async () => {
      const res = await api.get(`/tasks/${taskId}/comments`)
      return res.data?.data ?? []
    },
    // Fetch the thread ONLY when the popover opens (or when a caller forces it
    // with `alwaysFetchCount`). The list already ships an exact `commentCount`
    // per row via `initialCount`, so the closed cell shows the number WITHOUT a
    // request — this is what stops a busy Space firing one GET per row on open
    // (the "Space blinks for a couple of seconds" bug). Previously the cell also
    // fetched on mount whenever `initialCount > 0`, which reintroduced that
    // per-row storm for any Space whose tasks had comments.
    //
    // Admins follow the same gating as regular users — they get
    // read-only visibility, not zero visibility (see `isReadOnly`
    // wiring above).
    enabled: alwaysFetchCount || open,
  })
  const liveCount =
    countQuery.data?.length ??
    (typeof initialCount === 'number' ? initialCount : 0)

  useLayoutEffect(() => {
    if (!open) return
    // Compute the panel position from the trigger, and keep it glued to the
    // trigger while the panel is open. The panel is `position: fixed`
    // (portaled to <body>), so if the task list scrolls — often inside an
    // inner scroll container — its coordinates go stale and it drifts away
    // from its row. Recompute on scroll (capture: true catches scroll on ANY
    // ancestor scroll container, not just window) and on resize.
    function updateCoords() {
      if (!triggerRef.current) return
      const r = triggerRef.current.getBoundingClientRect()
      // Anchor the panel below the trigger and clamp to the viewport
      // so it never clips off-screen on narrow windows. Use
      // `documentElement.clientWidth` instead of `window.innerWidth`
      // because the latter INCLUDES the vertical scrollbar — on
      // Windows that's ~15-17px of phantom width, and the popover ends
      // up sitting on top of the scrollbar. `clientWidth` is the actual
      // visible area. The right-gutter is also generous (16px) so the
      // popover never visually touches the page edge.
      const PANEL_W = 360
      const RIGHT_GUTTER = 16
      const viewportW =
        document.documentElement?.clientWidth ?? window.innerWidth
      // Prefer anchoring at the trigger's left edge, but if that would
      // push the panel past the right gutter, slide it left so the
      // panel's right edge sits at `viewportW - RIGHT_GUTTER`.
      const maxLeft = viewportW - PANEL_W - RIGHT_GUTTER
      const left = Math.max(8, Math.min(maxLeft, r.left))
      // Vertical fit — cap the panel's height to the space actually
      // available and flip it above the trigger when there's more room up
      // than down, so its bottom (the pinned compose box) never runs off
      // screen. Hard ceiling of 60vh keeps it modest on tall screens.
      // (QA: on a short window the panel ran past the viewport bottom and
      // the "Write a comment" box was unreachable.)
      const GAP = 6
      const MARGIN = 12
      const viewportH = window.innerHeight
      const spaceBelow = viewportH - r.bottom - GAP - MARGIN
      const spaceAbove = r.top - GAP - MARGIN
      const hardMax = Math.round(viewportH * 0.6)
      const openUp = spaceBelow < 220 && spaceAbove > spaceBelow
      // Floor of 220px: the compose box (a `max-h-[120px]` textarea + the
      // Comment button row + header ≈ 204px) must always fit, so a
      // dragged-taller textarea can never push the Comment button below
      // the popover's clipped bottom edge (QA: button + text vanished
      // when the textarea was dragged). The thread scrolls (flex-1 +
      // min-h-0) to absorb the rest.
      const maxHeight = Math.max(
        220,
        Math.min(hardMax, openUp ? spaceAbove : spaceBelow),
      )
      // Anchor the edge NEAREST the trigger. Opening DOWN pins the panel's
      // TOP just below the trigger. Opening UP pins the panel's BOTTOM just
      // above the trigger (via CSS `bottom`) — so a SHORT popover hugs the
      // comment icon instead of floating up by (maxHeight − its actual
      // height), which read as the popover appearing detached from the field.
      const next = openUp
        ? { left, maxHeight, bottom: Math.max(MARGIN, viewportH - (r.top - GAP)) }
        : { left, maxHeight, top: r.bottom + GAP }
      setCoords(next)
    }
    updateCoords()
    window.addEventListener('scroll', updateCoords, true)
    window.addEventListener('resize', updateCoords)
    return () => {
      window.removeEventListener('scroll', updateCoords, true)
      window.removeEventListener('resize', updateCoords)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      const target = e.target
      if (panelRef.current?.contains(target)) return
      if (triggerRef.current?.contains(target)) return
      // The delete-confirm modal portals to <body>, outside panelRef. Don't
      // treat clicks on it (or its backdrop) as "outside" — that would close
      // the popover and unmount the confirm before it can act.
      if (target.closest?.('[data-modal-overlay]')) return
      // The @-mention autocomplete dropdown is portaled to <body>, outside
      // panelRef — so a mousedown to pick a mention would otherwise read as an
      // "outside" click and close the whole comment popover before the
      // selection registered (the dropdown appeared to vanish on click).
      if (target.closest?.('[data-mention-menu]')) return
      setOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // No admin short-circuit here any more — admins are read-only,
  // not no-access. The `isReadOnly` flag is forwarded into the
  // <TaskComments /> body below so the compose textarea + delete
  // buttons stay hidden, but the count chip and thread itself
  // render as normal.

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        title={
          liveCount === 0
            ? isReadOnly
              ? 'No comments'
              : 'Add comment'
            : `${liveCount} comment${liveCount === 1 ? '' : 's'}`
        }
        className={
          'inline-flex items-center gap-1.5 px-2 py-1 rounded text-sm cursor-pointer ' +
          (liveCount > 0
            ? 'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]'
            : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
        }
      >
        <MessageSquare size={13} />
        {liveCount > 0 ? (
          <span className="font-medium">{liveCount}</span>
        ) : (
          // Admins are read-only and shouldn't see "Comment" (verb,
          // implies they can post). Show a quiet dash instead so the
          // cell still reads as "no comments here" without offering
          // an action they can't perform.
          <span>{isReadOnly ? '—' : zeroLabel}</span>
        )}
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={panelRef}
              style={{
                position: 'fixed',
                left: coords.left,
                // `top` when opening below the trigger; `bottom` when flipped
                // above it — so the panel's trigger-adjacent edge stays glued
                // to the comment icon regardless of the popover's height.
                ...(coords.top != null
                  ? { top: coords.top }
                  : { bottom: coords.bottom }),
                zIndex: 80,
                width: 360,
                maxHeight: coords.maxHeight,
              }}
              className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <TaskComments
                taskId={taskId}
                currentUserId={currentUserId}
                spaceId={spaceId}
                // Fixed-height popover: the thread scrolls and the compose
                // box stays pinned to the bottom, so a long thread can't push
                // the "Write a comment" input below the fold (QA).
                pinnedInput
                // Force read-only for admins; otherwise leave undefined
                // so the thread resolves it from the Space access level
                // (`spaceId`). Passing `false` here would bypass that.
                readOnly={isReadOnly || undefined}
              />
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

// Matches an "@query" being typed immediately before the caret (query has no
// whitespace/@). Unicode-aware so names with accents are searchable.
const MENTION_TRIGGER = /(^|\s)@([^\s@]{0,40})$/u

/** Render a comment body with @-mentions highlighted. Occurrences of
 *  `@<mentioned user's name>` (from the server-resolved `mentionUsers`) become
 *  an accent-coloured span; everything else renders as plain text. Comments
 *  without mentions return the raw string unchanged — existing behaviour. */
function renderCommentBody(body, mentionUsers) {
  const names = (mentionUsers ?? []).map((u) => u.fullName).filter(Boolean)
  if (names.length === 0) return body
  const escaped = names
    .slice()
    .sort((a, b) => b.length - a.length) // longest first (prefix safety)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const re = new RegExp(`@(?:${escaped.join('|')})`, 'g')
  const out = []
  let last = 0
  let key = 0
  let m
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push(body.slice(last, m.index))
    out.push(
      <span
        key={`mention-${key++}`}
        className="font-medium text-[color:var(--color-accent-strong)]"
      >
        {m[0]}
      </span>,
    )
    last = m.index + m[0].length
  }
  if (last < body.length) out.push(body.slice(last))
  return out
}

/** Prefer opening the mention dropdown ABOVE the input (the compose box sits
 *  low in the popover); fall back below if there isn't room. Fixed-positioned
 *  so it escapes the compose box's `overflow-hidden`. */
function mentionMenuStyle(rect) {
  const width = Math.max(rect.width, 240)
  if (rect.top > 260) {
    return { left: rect.left, bottom: window.innerHeight - rect.top + 4, width }
  }
  return { left: rect.left, top: rect.bottom + 4, width }
}

/**
 * The existing comment `<textarea>` PLUS the @-mention autocomplete. Renders
 * the same textarea (caller passes the identical className + props) so the UI
 * is unchanged; only the dropdown behaviour is added. Reports the selected
 * mention user-ids up via `onMentionsChange`. The dropdown is portaled to
 * <body> so it isn't clipped by the compose box or the popover.
 */
function MentionBox({
  taskId,
  value,
  onChange,
  onMentionsChange,
  initialMentions,
  textareaRef,
  className,
  // Pulled out of the DOM spread so the textarea SOFT-limits (keeps accepting
  // input past the cap and flags it below) instead of hard-stopping.
  maxLength,
  ...textareaProps
}) {
  const innerRef = useRef(null)
  const ref = textareaRef ?? innerRef
  const [menu, setMenu] = useState(null) // { query, start } | null
  const [active, setActive] = useState(0)
  const [rect, setRect] = useState(null)
  const selectedRef = useRef([]) // [{ id, label }]

  const candidatesQuery = useQuery({
    queryKey: ['mention-candidates', taskId],
    queryFn: async () => {
      const res = await api.get(`/tasks/${taskId}/comments/mention-candidates`)
      return res.data?.data ?? []
    },
    enabled: menu !== null,
    staleTime: 60_000,
  })
  const candidates = candidatesQuery.data ?? []
  const q = (menu?.query ?? '').toLowerCase()
  const filtered = menu
    ? candidates
        .filter(
          (u) =>
            (u.fullName ?? '').toLowerCase().includes(q) ||
            (u.emailId ?? '').toLowerCase().includes(q),
        )
        .slice(0, 8)
    : []

  const report = (text) => {
    const kept = selectedRef.current.filter((mm) => text.includes(`@${mm.label}`))
    selectedRef.current = kept
    onMentionsChange?.([...new Set(kept.map((mm) => mm.id))])
  }

  // Seed existing mentions (edit mode) once, then prune to what's in the body.
  useEffect(() => {
    selectedRef.current = (initialMentions ?? []).map((mm) => ({
      id: mm.id,
      label: mm.label,
    }))
    report(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const positionMenu = () => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setRect({ left: r.left, top: r.top, bottom: r.bottom, width: r.width })
  }

  function handleChange(e) {
    const text = e.target.value
    onChange(text)
    const caret = e.target.selectionStart ?? text.length
    const found = MENTION_TRIGGER.exec(text.slice(0, caret))
    if (found) {
      setMenu({ query: found[2], start: caret - found[2].length - 1 })
      setActive(0)
      positionMenu()
    } else {
      setMenu(null)
    }
    report(text)
  }

  function choose(user) {
    const el = ref.current
    const caret = el?.selectionStart ?? value.length
    const start = menu?.start ?? caret
    const before = value.slice(0, start)
    const after = value.slice(caret)
    const inserted = `@${user.fullName} `
    const next = before + inserted + after
    onChange(next)
    selectedRef.current = [
      ...selectedRef.current.filter((x) => x.id !== user.userId),
      { id: user.userId, label: user.fullName },
    ]
    onMentionsChange?.([...new Set(selectedRef.current.map((mm) => mm.id))])
    setMenu(null)
    requestAnimationFrame(() => {
      const pos = (before + inserted).length
      el?.focus()
      el?.setSelectionRange(pos, pos)
    })
  }

  function handleKeyDown(e) {
    if (menu && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActive((a) => (a + 1) % filtered.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActive((a) => (a - 1 + filtered.length) % filtered.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        choose(filtered[active])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMenu(null)
        return
      }
    }
    textareaProps.onKeyDown?.(e)
  }

  useEffect(() => {
    if (!menu) return undefined
    const on = () => positionMenu()
    window.addEventListener('scroll', on, true)
    window.addEventListener('resize', on)
    return () => {
      window.removeEventListener('scroll', on, true)
      window.removeEventListener('resize', on)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu])

  // Don't forward our intercepted onKeyDown twice.
  // eslint-disable-next-line no-unused-vars
  const { onKeyDown: _ignoredKeyDown, ...restProps } = textareaProps

  return (
    <>
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={className}
        {...restProps}
      />
      {maxLength && (value ?? '').length > maxLength ? (
        <p role="alert" className="px-2.5 pb-1 text-xs text-[color:var(--color-priority-urgent)]">
          Comment must be at most {maxLength} characters.
        </p>
      ) : null}
      {menu && filtered.length > 0 && rect
        ? createPortal(
            <ul
              // Above the comments popover (z-80) and the task-dialog overlay
              // (z-90) — both host this compose box — so the mention list is
              // never hidden behind them.
              // `data-mention-menu` marks this portaled dropdown so the comment
              // popover's outside-click handler doesn't close on a mention pick.
              data-mention-menu=""
              className="fixed z-[120] max-h-56 w-64 overflow-y-auto rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg py-1"
              style={mentionMenuStyle(rect)}
              role="listbox"
            >
              {filtered.map((u, i) => (
                <li key={u.userId}>
                  <button
                    type="button"
                    // mousedown (not click) so the textarea keeps focus.
                    onMouseDown={(e) => {
                      e.preventDefault()
                      choose(u)
                    }}
                    onMouseEnter={() => setActive(i)}
                    className={
                      'flex w-full items-center gap-2 px-2.5 py-1.5 text-left cursor-pointer ' +
                      (i === active
                        ? 'bg-[color:var(--color-accent-bg)]'
                        : 'hover:bg-[color:var(--color-card-hover)]')
                    }
                  >
                    <Avatar
                      name={u.fullName}
                      avatarUrl={u.avatarUrl ?? null}
                      size="sm"
                      colorByName
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-[color:var(--color-ink-strong)]">
                        {u.fullName}
                      </span>
                      <span className="block truncate text-[11px] text-[color:var(--color-ink-muted)]">
                        {u.emailId}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </>
  )
}

/**
 * Standalone comments thread for one task — list + new-comment
 * textarea + delete-own affordance. Used inside `CommentsCell`'s
 * portal popup and inside the Task Detail dialog (so the read /
 * write / delete behaviour stays in lockstep with the popup).
 */
export function TaskComments({
  taskId,
  currentUserId,
  readOnly: readOnlyProp,
  spaceId,
  /** When true, lay the thread + compose box out as a fixed-height flex
   *  column: the thread SCROLLS and the "Write a comment" box stays PINNED
   *  to the bottom, always visible. Used by the CommentsCell popover, where
   *  a long thread otherwise pushed the input below the fold (QA). The Task
   *  Detail dialog leaves this off so the whole dialog body scrolls as one. */
  pinnedInput = false,
}) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')
  // @-mention user-ids for the compose box + the inline edit box. Reported by
  // MentionBox; sent to the backend alongside the body (backend re-validates).
  const [draftMentions, setDraftMentions] = useState([])
  const [editMentions, setEditMentions] = useState([])
  // Compose box auto-grows to fit what's typed, but is HARD-CAPPED at
  // 96px (see `COMPOSE_MAX`) so a long comment (500+ chars) stays usable
  // yet can never push the Comment button out of the popover — past the
  // cap it scrolls. No manual drag handle (`resize-none`), so the height
  // is fully controlled here.
  const composeRef = useRef(null)
  const COMPOSE_MAX = 96
  useEffect(() => {
    const el = composeRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, COMPOSE_MAX) + 'px'
  }, [draft])
  // Inline edit state — which comment is being edited + its working text.
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState('')
  // Which comment is pending delete confirmation (null = none). Drives the
  // ConfirmDialog below — deletion only fires after the user confirms.
  const [pendingDeleteId, setPendingDeleteId] = useState(null)
  // Tenant admins (Company Admin / Super Admin) get READ-ONLY access
  // — they see the thread but can't post, edit, or delete. The
  // backend's `assertTaskAccess` already grants admins access via
  // the synth-admin membership (see `workspace-access.js`), so the
  // GET succeeds. The prior build short-circuited with `return null`
  // here, contradicting the docstring on `isReadOnlyCommentRole` and
  // hiding context the admin role legitimately needs for oversight.
  // `readOnlyProp` lets a caller (e.g. CommentsCell) pass the flag
  // explicitly so the two components stay in lockstep; we still fall
  // back to detecting from the auth store for standalone callers.
  const actorRole = useAuthStore((s) => s.user?.role)
  // Read-only Space access ('view') can see the thread but not post.
  // `comment`/`edit`/`full` can comment. When `spaceId` isn't supplied
  // (callers that don't know it) this is permissive and the backend
  // still enforces `comment.create`.
  const { canComment } = useSpaceAccess(spaceId)
  const readOnly =
    typeof readOnlyProp === 'boolean'
      ? readOnlyProp
      : isReadOnlyCommentRole(actorRole)
        ? true
        : isFullCommentAdminRole(actorRole)
          ? false
          : !canComment
  const listQuery = useQuery({
    queryKey: ['task-comments', taskId],
    queryFn: async () => {
      const res = await api.get(`/tasks/${taskId}/comments`)
      return res.data?.data ?? []
    },
    // Always fetch — admins are read-only, not no-access.
  })
  const createMutation = useMutation({
    mutationFn: async ({ body, mentions }) => {
      const res = await api.post(`/tasks/${taskId}/comments`, { body, mentions })
      return res.data
    },
    onSuccess: () => {
      setDraft('')
      setDraftMentions([])
      void qc.invalidateQueries({ queryKey: ['task-comments', taskId] })
    },
    onError: (err) => {
      const { message } = extractApiError(err)
      toast.error(message ?? 'Could not post comment.')
    },
  })
  const deleteMutation = useMutation({
    mutationFn: async (commentId) => {
      await api.delete(`/tasks/${taskId}/comments/${commentId}`)
    },
    onSuccess: () => {
      toast.success('Comment deleted successfully.')
      setPendingDeleteId(null)
      void qc.invalidateQueries({ queryKey: ['task-comments', taskId] })
    },
    onError: (err) => {
      const { message } = extractApiError(err)
      toast.error(message ?? 'Could not delete comment.')
    },
  })
  const updateMutation = useMutation({
    mutationFn: async ({ commentId, body, mentions }) => {
      const res = await api.patch(`/tasks/${taskId}/comments/${commentId}`, {
        body,
        mentions,
      })
      return res.data
    },
    onSuccess: () => {
      setEditingId(null)
      setEditDraft('')
      setEditMentions([])
      void qc.invalidateQueries({ queryKey: ['task-comments', taskId] })
    },
    onError: (err) => {
      const { message } = extractApiError(err)
      toast.error(message ?? 'Could not update comment.')
    },
  })

  const rows = listQuery.data ?? []
  const trimmed = draft.trim()
  const canSend = trimmed.length > 0 && !createMutation.isPending

  // No admin short-circuit here any more — admins are read-only
  // (see `readOnly` resolution above + the prop forwarded by
  // CommentsCell). The compose textarea and delete buttons further
  // down stay hidden when `readOnly` is true, but the thread itself
  // renders normally.

  // Layout: `pinnedInput` (CommentsCell popover) lays this out as a
  // fixed-height flex column — the thread scrolls, the compose box stays
  // pinned to the bottom. Default (Task Detail dialog) keeps the in-flow
  // layout where the dialog body owns the scroll.
  const outerCls = pinnedInput
    ? 'flex flex-col min-h-0 flex-1 border-t border-[color:var(--color-rule)]'
    : 'px-4 py-3.5 border-t border-[color:var(--color-rule)]'
  const headerCls = pinnedInput
    ? 'flex items-center justify-between px-4 pt-3.5 pb-3 shrink-0'
    : 'flex items-center justify-between mb-3'
  const threadCls = pinnedInput
    ? 'space-y-2.5 flex-1 min-h-0 overflow-y-auto px-4 pb-3'
    : 'space-y-2.5 mb-3'
  const composeCls = pinnedInput
    ? 'shrink-0 px-4 pt-3 pb-3.5 border-t border-[color:var(--color-rule)] bg-[color:var(--color-card)]'
    : ''

  return (
    <div className={outerCls}>
      {/* Delete-comment confirmation. overlayZClass raises it above the
       *  CommentsCell popover (zIndex 80) so it isn't painted behind it when
       *  the thread is opened from the List/Board comment cell. */}
      <ConfirmDialog
        open={pendingDeleteId != null}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmLabel="Delete"
        confirming={deleteMutation.isPending}
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={() => deleteMutation.mutate(pendingDeleteId)}
        overlayZClass="z-[90]"
      />
      {/* Header row — small uppercase title with a subtle count chip.
       *  Lives flush with the popover edges so the comment rows below
       *  get the full 360px width to breathe. */}
      <div className={headerCls}>
        <h3 className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
          Comments
        </h3>
        {rows.length > 0 ? (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[color:var(--color-card-hover)] text-[11px] font-semibold text-[color:var(--color-ink-muted)] tabular-nums">
            {rows.length}
          </span>
        ) : null}
      </div>

      <div className={threadCls}>
        {listQuery.isLoading ? (
          <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
            Loading comments…
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
            {readOnly
              ? 'No comments on this task yet.'
              : 'No comments yet. Be the first to add one.'}
          </p>
        ) : (
          rows.map((c) => {
            // Read-only admins never see the delete button regardless
            // of authorship — they're observers of the thread, not
            // participants. For everyone else, "delete own comment"
            // stays gated on authorship.
            const isOwn =
              !readOnly && currentUserId && c.authorId === currentUserId
            return (
              // Single-comment card. Faint canvas-tinted background +
              // soft border makes each thread item readable as a unit
              // (vs. a flat run of paragraphs that bled together in
              // the old 360px popover). Padding is intentionally
              // tighter than the old `flex gap-3` layout so we don't
              // waste horizontal space on the narrow popup width.
              <div
                key={c.id}
                data-focus-id={c.id}
                className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]/60 px-3 py-2.5"
              >
                <div className="flex items-start gap-2.5">
                  <Avatar
                    name={c.authorFullName}
                    avatarUrl={c.authorAvatarUrl ?? null}
                    size="sm"
                    colorByName
                  />
                  <div className="flex-1 min-w-0">
                    {/* Row 1: name + (own-comment delete icon). Name
                     *  gets the full row width and only ellipsizes
                     *  if it's truly absurdly long — the old layout
                     *  shared this row with date/time/Delete which
                     *  forced a "Aarav Me…" truncation at typical
                     *  name lengths. */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate">
                        {c.authorFullName}
                      </span>
                      {isOwn ? (
                        <div className="ml-auto flex items-center gap-0.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(c.id)
                              setEditDraft(c.body)
                            }}
                            disabled={updateMutation.isPending}
                            aria-label="Edit comment"
                            title="Edit comment"
                            className="inline-flex items-center justify-center h-6 w-6 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50 transition-colors"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingDeleteId(c.id)}
                            disabled={deleteMutation.isPending}
                            aria-label="Delete Comment"
                            title="Delete Comment"
                            className="inline-flex items-center justify-center h-6 w-6 rounded-md text-[color:var(--color-ink-muted)] hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/15 cursor-pointer disabled:opacity-50 transition-colors"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                    {/* Row 2: date · time on its own muted line so it
                     *  never wraps the way "20/05/2026 · 07:27 PM"
                     *  did when crammed onto the name row. */}
                    <p className="text-[11px] text-[color:var(--color-ink-muted)] m-0 mt-0.5 tabular-nums whitespace-nowrap">
                      {formatDateTime(c.createdAt)}
                      {c.editedAt ? (
                        <span className="text-[color:var(--color-ink-subtle)]">
                          {' '}
                          · edited
                        </span>
                      ) : null}
                    </p>
                    {/* Row 3: comment body — or, when this comment is being
                     *  edited, an inline textarea with Save / Cancel.
                     *  `whitespace-pre-wrap` preserves intentional line
                     *  breaks; `break-words` keeps a long URL/token from
                     *  blowing out the popover. */}
                    {editingId === c.id ? (
                      <div className="mt-1.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] focus-within:border-[color:var(--color-accent)] transition-colors overflow-hidden">
                        <MentionBox
                          key={c.id}
                          taskId={taskId}
                          value={editDraft}
                          onChange={setEditDraft}
                          onMentionsChange={setEditMentions}
                          initialMentions={(c.mentionUsers ?? []).map((u) => ({
                            id: u.id,
                            label: u.fullName,
                          }))}
                          rows={3}
                          maxLength={500}
                          autoFocus
                          // `resize-none` — no drag handle (matches the compose
                          // box); the box scrolls internally for long edits.
                          className="w-full resize-none h-20 overflow-y-auto bg-transparent px-2.5 py-1.5 text-sm text-[color:var(--color-ink)] outline-none"
                        />
                        <div className="flex items-center justify-end gap-1.5 px-2 py-1 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]/40">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(null)
                              setEditDraft('')
                            }}
                            className="px-2 py-0.5 rounded text-xs font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            disabled={
                              editDraft.trim().length === 0 ||
                              updateMutation.isPending
                            }
                            onClick={() => {
                              const body = editDraft.trim()
                              if (body) {
                                updateMutation.mutate({
                                  commentId: c.id,
                                  body,
                                  mentions: editMentions,
                                })
                              }
                            }}
                            className="px-2.5 py-0.5 rounded-md bg-[color:var(--color-accent-strong)] text-white text-xs font-medium hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                          >
                            {updateMutation.isPending ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[color:var(--color-ink)] whitespace-pre-wrap break-words m-0 mt-1.5 leading-snug">
                        {renderCommentBody(c.body, c.mentionUsers)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Compose row — textarea + send button. Hidden entirely for
       *  read-only viewers (Company Admin / Super Admin) — they're
       *  observers of the thread, not participants. The backend
       *  also rejects post attempts from these roles as defense in
       *  depth, so even a hand-crafted POST would 403. `composeCls` pins
       *  this block to the popover's bottom in pinnedInput mode. */}
      <div className={composeCls}>
      {!readOnly ? (
        <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] focus-within:border-[color:var(--color-accent)] transition-colors overflow-hidden">
          <MentionBox
            taskId={taskId}
            textareaRef={composeRef}
            value={draft}
            onChange={setDraft}
            onMentionsChange={setDraftMentions}
            rows={2}
            placeholder="Write a comment… use @ to mention someone"
            maxLength={500}
            // No drag handle (`resize-none`); the box auto-grows to fit
            // the text up to `COMPOSE_MAX` (96px, see the effect above),
            // then scrolls. A long comment can't push the Comment button
            // out of the popover, and there's no handle to drag it taller.
            className="w-full resize-none min-h-[44px] max-h-[96px] overflow-y-auto bg-transparent px-3 py-2 text-sm text-[color:var(--color-ink)] outline-none placeholder:text-[color:var(--color-ink-muted)]"
          />
          <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]/40">
            <span className="text-[11px] text-[color:var(--color-ink-muted)] tabular-nums">
              {trimmed.length > 0 ? `${trimmed.length}/500` : ''}
            </span>
            <button
              type="button"
              disabled={!canSend}
              onClick={() =>
                canSend &&
                createMutation.mutate({ body: trimmed, mentions: draftMentions })
              }
              className="px-3 py-1 rounded-md bg-[color:var(--color-accent-strong)] text-white text-sm font-medium hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {createMutation.isPending ? 'Posting…' : 'Comment'}
            </button>
          </div>
        </div>
      ) : (
        // Quiet read-only footer — no compose row. A faint italic
        // note tells admins why they can't post, so the popover
        // doesn't end with an abrupt "no compose textarea, why?"
        // gap below the thread.
        <p className="mt-1 text-[11px] italic text-[color:var(--color-ink-subtle)]">
          Read-only — admins can view comments but not post.
        </p>
      )}
      </div>
    </div>
  )
}
